import { Injectable } from '@angular/core';
import { GeolocationService } from '@ng-web-apis/geolocation';
import { HttpClient } from '@angular/common/http';
import { take, map } from 'rxjs/operators';
import { GlobalConstants } from '../../global-constants';
import { Observable, from } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class GeoService {

  constructor(
    private geolocation$: GeolocationService,
    private http: HttpClient
  ) {}

  /* ============================================
   🔹 1. CHEQUEAR PERMISO GPS
  ============================================ */

  checkPermission(): Promise<PermissionState> {
    return navigator.permissions
      .query({ name: 'geolocation' as PermissionName })
      .then(result => result.state); // 'granted' | 'denied' | 'prompt'
  }

  /* ============================================
   🔹 2. UBICACIÓN GPS EXACTA (CON POPUP)
  ============================================ */

  getExactPosition(): Observable<GeolocationPosition> {
    return this.geolocation$.pipe(take(1));
  }

  /* ============================================
   🔹 3. UBICACIÓN APROXIMADA POR IP (SIN POPUP)
  ============================================ */

  getApproximatePosition() {
    return this.http.get('https://ipapi.co/json/').pipe(
      map((data: any) => ({
        coords: {
          latitude: data.latitude,
          longitude: data.longitude
        },
        city: data.city,
        region: data.region,
        country: data.country_name,
        postcode: data.postal,
      }))
    );
  }

  /* ============================================
   🔹 4. OBTENER CIUDAD DESDE LAT/LON (NOMINATIM)
  ============================================ */

  getCity(lat: number, lon: number) {
    const url = `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=json`;

    return this.http.get(url, {
      headers: { 'Accept-Language': 'es' }
    }).pipe(
      map((data: any) =>
        data.address.city ||
        data.address.town ||
        data.address.village ||
        data.address.hamlet ||
        data.address.county ||
        data.address.postcode ||
        data.address.suburb
      )
    );
  }

  /* ============================================
   🔹 5. USAR TU BACKEND (RECOMENDADO)
  ============================================ */

  getGeo(lat: number, lon: number) {
    const url = GlobalConstants.urlServer +
      `/backend/api/geolocalizacion.php?lat=${lat}&lon=${lon}`;

    return this.http.get(url).pipe(
      map((data: any) => {
        const address = data.address;

        const city =
          address.city ||
          address.town ||
          address.village ||
          address.hamlet ||
          address.county ||
          address.postcode ||
          address.suburb;

        return {
          city,
          state: address.state || null,
          country: address.country || null,
          postcode: address.postcode || null,
          barrio: address.suburb || null,
          display_name: data.display_name || null,
          lat: data.lat,
          lon: data.lon
        };
      })
    );
  }

  /* ============================================
    🔹 6. BUSCAR DIRECCIONES POR TEXTO (AUTOCOMPLETE)
  ============================================ */
  buscarDireccion(query: string): Observable<any[]> {
    if (!query.trim()) return from([[]]); // Si está vacío, devuelve array vacío

    const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=jsonv2&limit=5&addressdetails=1`;

    return this.http.get<any[]>(url, {
      headers: { 'Accept-Language': 'es' } // Resultados en español
    }).pipe(
      map((results: any[]) =>
        results.filter(item => item.address && item.address.postcode)
      ),
      map((results: any[]) => {
        return results.map(item => {
          const addr = item.address || {};

          return {
            display_name: item.display_name,
            lat: item.lat,
            lon: item.lon,

            house_number: addr.house_number || '',
            road: addr.road || addr.pedestrian || addr.footway || '',

            neighbourhood: addr.neighbourhood || addr.suburb || addr.quarter || '',
            suburb: addr.suburb || '',

            city: addr.city || addr.town || addr.village || addr.hamlet || '',

            municipality: addr.municipality || addr.city_district || addr.district || '',

            county: addr.county || '',

            state: addr.state || '',
            state_district: addr.state_district || '',

            postcode: addr.postcode || '',

            country: addr.country || '',
            country_code: addr.country_code || '',

            island: addr.island || '',

            province: addr.province || '',

            address_raw: addr
          };
        });
      })
    );
  }
}